// utils/protectedFiles.js
import React, { useMemo } from "react";
import axiosInstance from "../axiosInstance";

/**
 * Objectif :
 *  - NE PLUS utiliser Axios / XHR pour charger les images (sinon CORS + credentials → erreur)
 *  - Générer une URL protégée (via /api/files/:id?t=JWT)
 *  - Laisser <img src="..."> charger l'image directement (Cloudinary compris)
 */

// base API (ex: "https://flowdevis-backend-production.up.railway.app/api")
const API_BASE = (axiosInstance?.defaults?.baseURL || "").replace(/\/+$/, "");

/** Récupère le JWT stocké */
function getToken() {
  try {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("jwt") ||
      ""
    );
  } catch {
    return "";
  }
}

/**
 * Construit une URL protégée à partir de :
 *  - un id numérique ("55")
 *  - un chemin /api/files/55, "files/55", etc.
 *  - une URL absolue (https://res.cloudinary.com/...) → renvoyée telle quelle
 */
export function buildProtectedUrl(fileOrPath) {
  if (!fileOrPath) return null;

  let raw = String(fileOrPath).trim();
  if (!raw) return null;

  // Déjà une URL complète (Cloudinary, autre CDN…)
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  // Seulement un id → /api/files/:id
  if (/^\d+$/.test(raw)) {
    raw = `/files/${raw}`;
  }

  // Normalisation du chemin
  if (!raw.startsWith("/")) raw = "/" + raw;
  if (!raw.startsWith("/api/")) raw = "/api" + raw.replace(/^\/+/, "/");

  const base = API_BASE || "";
  const url = base + raw;

  // On ajoute le token JWT sous forme de query ?t=...
  const token = getToken();
  if (!token) return url;

  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}t=${encodeURIComponent(token)}`;
}

/**
 * Composant <ProtectedImg> :
 *  - NE fait plus d'appel Axios
 *  - Se contente d'utiliser <img src="URL_PROTÉGÉE">
 *  - L'URL peut être :
 *      • /api/files/:id (stockage local protégé)
 *      • une URL Cloudinary déjà complète
 */
export function ProtectedImg({ fileOrPath, alt = "", ...rest }) {
  const src = useMemo(
    () => buildProtectedUrl(fileOrPath),
    [fileOrPath]
  );

  if (!src) return null;

  return (
    <img
      src={src}
      alt={alt}
      crossOrigin="anonymous"
      {...rest}
    />
  );
}

/**
 * hydrateProtectedHtml :
 *  - parcourt le HTML
 *  - remplace les <img> portant des attributs "data-file-id" / "data-file" / "data-protected-src"
 *    par une URL protégée (buildProtectedUrl)
 */
export async function hydrateProtectedHtml(html) {
  if (!html) return "";
  if (typeof window === "undefined") return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const imgs = doc.querySelectorAll(
    "img[data-file-id], img[data-file], img[data-protected-src]"
  );

  imgs.forEach((img) => {
    const ref =
      img.getAttribute("data-file-id") ||
      img.getAttribute("data-file") ||
      img.getAttribute("data-protected-src");

    const url = buildProtectedUrl(ref);
    if (url) {
      img.removeAttribute("data-file-id");
      img.removeAttribute("data-file");
      img.removeAttribute("data-protected-src");
      img.setAttribute("src", url);
      img.setAttribute("crossorigin", "anonymous");
    }
  });

  return doc.body.innerHTML;
}

// Compat / no-op
export function stableSigFromSociete(s = {}) {
  const id =
    s?.id || s?.societe_id || s?.user_id || s?.siret || s?.email || "anon";
  return `soc:${id}`;
}

export function revokeAllProtectedUrls() {
  // plus rien à faire ici
}
